// Content schema + loader.
//
// Buildings, recipes and items are bundled data, not code. loadContent()
// validates cross-references — not just per-row shape — so a recipe pointing at
// a missing item, or a building pointing at a missing recipe, is rejected. A
// bad reference throws immediately: content errors are authoring bugs that must
// never reach runtime. The sim reads only the validated result.

/** A content id: lowercase, dot-namespaced, stable across versions. */
export type ContentId = string

/** An item row. */
export interface ItemDef {
  id: ContentId
}

/**
 * A recipe row. A recipe either produces `output` (optionally consuming
 * `input`), or is a `sink` that only consumes `input`. `fromPatch` marks an
 * extractor that draws from an infinite, throughput-capped source.
 *
 * Production tuning lives here as data (not module constants), so timing and
 * quantities are per-recipe balance values, not code:
 * - `ticks`: cooldown per production batch; a batch takes this many ticks to
 *   complete. Omit to produce as fast as possible (one tick per batch).
 * - `bufferCap`: for a `fromPatch` extractor, how many output units to stock
 *   before it stops extracting. Omit to keep a single unit ready.
 * - `inputQty` / `outputQty`: units consumed / produced per batch. Omit for one.
 */
export interface RecipeDef {
  id: ContentId
  input?: ContentId
  output?: ContentId
  fromPatch?: boolean
  sink?: boolean
  ticks?: number
  bufferCap?: number
  inputQty?: number
  outputQty?: number
}

/**
 * A resource-patch row: an infinite, throughput-capped source the chain head
 * extracts from. `item` is what a patch yields; `extractionSlots` caps how many
 * extractors may draw from one patch at once (surplus extractors idle). A patch
 * never depletes — the cap, not exhaustion, is the throughput limit that later
 * forces reaching distant patches.
 */
export interface PatchDef {
  id: ContentId
  item: ContentId
  extractionSlots?: number
}

/** Extraction-slot count for a patch def, defaulting to a single slot. */
export function extractionSlotCount(patch: PatchDef): number {
  return patch.extractionSlots ?? 1
}

/** A building row: a building runs exactly one recipe. */
export interface BuildingDef {
  id: ContentId
  recipe: ContentId
  /**
   * Number of colonists that can access the building at once. Surplus haulers
   * queue rather than overlapping in the slots. This tuning value lives in
   * content data; omit to default to one slot.
   */
  accessSlots?: number
}

/** Slot count for a building def, defaulting to a single access slot. */
export function accessSlotCount(building: BuildingDef): number {
  return building.accessSlots ?? 1
}

/** Un-validated content, as authored. */
export interface RawContent {
  items: ItemDef[]
  recipes: RecipeDef[]
  buildings: BuildingDef[]
  patches: PatchDef[]
}

/** Validated content the sim reads, indexed by id. */
export interface Content {
  items: ReadonlyMap<ContentId, ItemDef>
  recipes: ReadonlyMap<ContentId, RecipeDef>
  buildings: ReadonlyMap<ContentId, BuildingDef>
  patches: ReadonlyMap<ContentId, PatchDef>
}

/** Thrown by loadContent() on any validation failure. */
export class ContentValidationError extends Error {
  constructor(message: string) {
    super(`content validation failed: ${message}`)
    this.name = 'ContentValidationError'
  }
}

// A valid id has at least one dot; each segment is lowercase alphanumerics or
// underscores and starts with a letter.
const ID_PATTERN = /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/

function assertId(id: unknown, kind: string): asserts id is ContentId {
  if (typeof id !== 'string' || !ID_PATTERN.test(id)) {
    throw new ContentValidationError(
      `${kind} has an invalid id ${JSON.stringify(id)} — ids must be lowercase, dot-namespaced strings`,
    )
  }
}

// Optional tuning fields default when omitted; when present they must be a
// positive integer (a fractional or zero cooldown/quantity has no meaning).
function assertPositiveInt(value: number | undefined, label: string): void {
  if (value === undefined) return
  if (!Number.isInteger(value) || value < 1) {
    throw new ContentValidationError(
      `${label} is ${value} — must be a positive integer`,
    )
  }
}

function indexUnique<T extends { id: ContentId }>(
  rows: T[],
  kind: string,
): Map<ContentId, T> {
  const byId = new Map<ContentId, T>()
  for (const row of rows) {
    assertId(row.id, kind)
    if (byId.has(row.id)) {
      throw new ContentValidationError(`duplicate ${kind} id ${row.id}`)
    }
    byId.set(row.id, row)
  }
  return byId
}

/**
 * Validate raw content and return the indexed view. Throws
 * ContentValidationError on a malformed id, a duplicate id, or a dangling
 * cross-reference.
 */
export function loadContent(raw: RawContent): Content {
  const items = indexUnique(raw.items, 'item')
  const recipes = indexUnique(raw.recipes, 'recipe')
  const buildings = indexUnique(raw.buildings, 'building')
  const patches = indexUnique(raw.patches, 'patch')

  // Every recipe input/output item id must resolve.
  for (const recipe of recipes.values()) {
    if (recipe.input !== undefined && !items.has(recipe.input)) {
      throw new ContentValidationError(
        `recipe ${recipe.id} references unknown input item ${recipe.input}`,
      )
    }
    if (recipe.output !== undefined && !items.has(recipe.output)) {
      throw new ContentValidationError(
        `recipe ${recipe.id} references unknown output item ${recipe.output}`,
      )
    }
    if (recipe.output === undefined && recipe.sink !== true) {
      throw new ContentValidationError(
        `recipe ${recipe.id} has no output and is not a sink — a recipe must produce an item or be a pure sink`,
      )
    }
    if (recipe.sink === true && recipe.output !== undefined) {
      throw new ContentValidationError(
        `recipe ${recipe.id} is a sink but also declares output ${recipe.output} — a sink only consumes`,
      )
    }
    if (recipe.sink === true && recipe.input === undefined) {
      throw new ContentValidationError(
        `recipe ${recipe.id} is a sink with no input — a sink must consume an item`,
      )
    }
    if (recipe.fromPatch === true && recipe.input !== undefined) {
      throw new ContentValidationError(
        `recipe ${recipe.id} is a fromPatch extractor but declares input ${recipe.input} — a patch draws from an infinite source`,
      )
    }
    // A recipe that produces an output with no input and is not a patch has no
    // source for its output. It matches no production branch and would silently
    // never produce, so it is not a valid v1 recipe shape — reject it here so
    // production() only ever sees a fromPatch extractor, an input→output
    // converter, or a pure sink.
    if (
      recipe.output !== undefined &&
      recipe.input === undefined &&
      recipe.fromPatch !== true
    ) {
      throw new ContentValidationError(
        `recipe ${recipe.id} produces ${recipe.output} with no input and is not a fromPatch extractor — a producer must consume an input or draw from a patch`,
      )
    }
    assertPositiveInt(recipe.ticks, `recipe ${recipe.id} ticks`)
    assertPositiveInt(recipe.bufferCap, `recipe ${recipe.id} bufferCap`)
    assertPositiveInt(recipe.inputQty, `recipe ${recipe.id} inputQty`)
    assertPositiveInt(recipe.outputQty, `recipe ${recipe.id} outputQty`)

    // A converter that produces its own input self-feeds — with outputQty >=
    // inputQty a single seed unit yields unbounded free output. Reject it: a
    // recipe transforms one item into a different one.
    if (recipe.input !== undefined && recipe.input === recipe.output) {
      throw new ContentValidationError(
        `recipe ${recipe.id} consumes and produces the same item ${recipe.input} — a recipe must transform one item into another`,
      )
    }

    // Tuning fields only take effect on the recipe shape that reads them;
    // reject an inapplicable field so a mis-authored balance value fails loud at
    // load rather than silently no-opping. A sink has no output and no cooldown;
    // only a fromPatch extractor buffers; only a converter consumes an input.
    const isConverter =
      recipe.input !== undefined && recipe.output !== undefined
    if (recipe.output === undefined && recipe.ticks !== undefined) {
      throw new ContentValidationError(
        `recipe ${recipe.id} sets ticks but produces no output — a sink has no production cooldown`,
      )
    }
    if (recipe.output === undefined && recipe.outputQty !== undefined) {
      throw new ContentValidationError(
        `recipe ${recipe.id} sets outputQty but produces no output`,
      )
    }
    if (recipe.bufferCap !== undefined && recipe.fromPatch !== true) {
      throw new ContentValidationError(
        `recipe ${recipe.id} sets bufferCap but is not a fromPatch extractor — only an extractor buffers its output`,
      )
    }
    if (recipe.inputQty !== undefined && !isConverter) {
      throw new ContentValidationError(
        `recipe ${recipe.id} sets inputQty but does not consume an input to produce an output`,
      )
    }

    // An extractor emits a whole batch (outputQty) at once but only checks the
    // cap before starting, so a bufferCap below a batch is overshot every cycle.
    // Require the cap to hold at least one batch. Both default to 1 when omitted.
    if (
      recipe.fromPatch === true &&
      (recipe.bufferCap ?? 1) < (recipe.outputQty ?? 1)
    ) {
      throw new ContentValidationError(
        `recipe ${recipe.id} has bufferCap ${recipe.bufferCap ?? 1} below outputQty ${recipe.outputQty ?? 1} — a batch would overshoot the buffer`,
      )
    }
  }

  // Every building's recipe id must resolve.
  for (const building of buildings.values()) {
    if (!recipes.has(building.recipe)) {
      throw new ContentValidationError(
        `building ${building.id} references unknown recipe ${building.recipe}`,
      )
    }
    if (
      building.accessSlots !== undefined &&
      (!Number.isInteger(building.accessSlots) || building.accessSlots < 1)
    ) {
      throw new ContentValidationError(
        `building ${building.id} has accessSlots ${building.accessSlots} — must be a positive integer`,
      )
    }
  }

  // Every patch's yielded item must resolve, and its extraction-slot count (when
  // set) must be a positive integer — the cap on concurrent extractors.
  for (const patch of patches.values()) {
    if (!items.has(patch.item)) {
      throw new ContentValidationError(
        `patch ${patch.id} yields unknown item ${patch.item}`,
      )
    }
    assertPositiveInt(
      patch.extractionSlots,
      `patch ${patch.id} extractionSlots`,
    )
  }

  // Cross-reference: every fromPatch extractor recipe must have at least one
  // patch yielding its output. A fromPatch recipe with no matching patch would
  // seed no source and silently never produce — an authoring bug, rejected at
  // load like the dangling-reference checks above.
  for (const recipe of recipes.values()) {
    if (recipe.fromPatch !== true || recipe.output === undefined) continue
    const hasPatch = [...patches.values()].some((p) => p.item === recipe.output)
    if (!hasPatch) {
      throw new ContentValidationError(
        `recipe ${recipe.id} is a fromPatch extractor for ${recipe.output} but no patch yields that item — a patch content row must feed it`,
      )
    }
  }

  return { items, recipes, buildings, patches }
}

/**
 * Validate a chain of building ids against loaded content. Every id must resolve
 * to a building, and each adjacent link must connect: the upstream building's
 * recipe output must equal the downstream building's recipe input, so a
 * mis-authored chain fails fast+loud at boot rather than dispatching hauls for
 * an item the downstream never consumes. Throws ContentValidationError.
 */
export function validateChain(
  content: Content,
  chainBuildingIds: ContentId[],
): void {
  for (let i = 0; i < chainBuildingIds.length; i++) {
    const id = chainBuildingIds[i]
    const building = content.buildings.get(id)
    if (building === undefined) {
      throw new ContentValidationError(
        `chain references unknown building ${id}`,
      )
    }
    if (i === 0) continue
    const upId = chainBuildingIds[i - 1]
    const up = content.recipes.get(content.buildings.get(upId)!.recipe)!
    const down = content.recipes.get(building.recipe)!
    if (up.output === undefined) {
      throw new ContentValidationError(
        `chain link ${upId} → ${id} does not connect: ${upId} produces no output to haul`,
      )
    }
    if (down.input !== up.output) {
      throw new ContentValidationError(
        `chain link ${upId} → ${id} does not connect: ${upId} outputs ${up.output} but ${id} consumes ${down.input ?? 'nothing'}`,
      )
    }
  }
}

/** A pull edge between two adjacent buildings in a chain. */
export interface PullEdge {
  /** Chain index of the upstream (source) building. */
  fromIdx: number
  /** Chain index of the downstream (consumer) building. */
  toIdx: number
  /** Item that flows along the edge — the upstream recipe's output. */
  item: ContentId
}

/**
 * Derive pull edges from a chain's order and its recipes. An edge exists for an
 * adjacent pair only when the downstream building's recipe consumes what the
 * upstream produces: the consumer pulls an item because its recipe declares that
 * item as demand, not merely because the upstream happens to emit it. The
 * flowing item is that shared input/output. Edges come only from data, so
 * extending the chain with another building+recipe row needs no change here.
 */
export function deriveChainPullEdges(
  content: Content,
  chainBuildingIds: ContentId[],
): PullEdge[] {
  const edges: PullEdge[] = []
  for (let i = 1; i < chainBuildingIds.length; i++) {
    const upstreamId = chainBuildingIds[i - 1]
    const upBuilding = content.buildings.get(upstreamId)
    if (upBuilding === undefined) {
      throw new ContentValidationError(
        `chain references unknown building ${upstreamId}`,
      )
    }
    const downBuilding = content.buildings.get(chainBuildingIds[i])
    if (downBuilding === undefined) {
      throw new ContentValidationError(
        `chain references unknown building ${chainBuildingIds[i]}`,
      )
    }
    const upRecipe = content.recipes.get(upBuilding.recipe)
    const downRecipe = content.recipes.get(downBuilding.recipe)
    // An edge carries an item only when the upstream emits it and the downstream
    // recipe declares it as demand (its input). A pure sink demands its input
    // like any consumer; a building that produces something the neighbour does
    // not consume yields no edge.
    if (upRecipe === undefined || upRecipe.output === undefined) continue
    if (downRecipe === undefined || downRecipe.input !== upRecipe.output) {
      continue
    }
    edges.push({ fromIdx: i - 1, toIdx: i, item: upRecipe.output })
  }
  return edges
}
